import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import { useI18n } from "../lib/i18n.jsx";
import { useAuth } from "../lib/auth.jsx";
import StatusBadge, { FeedbackChip, PriorityBadge, EscalationBadge } from "../components/StatusBadge.jsx";
import { ChevronLeft, Edit3, Plus, Upload, FileText, Loader2, CalendarRange, ExternalLink } from "lucide-react";
import { fmtDate, fmtDateTime, fmtRelative } from "../lib/format.js";
import NewPartsRequestDialog from "../components/NewPartsRequestDialog.jsx";
import NewClassificationRequestDialog from "../components/NewClassificationRequestDialog.jsx";
import WorkflowDecisionDialog from "../components/WorkflowDecisionDialog.jsx";
import WorkflowTimeline from "../components/WorkflowTimeline.jsx";
import FuelRequestsSection from "../components/FuelRequestsSection.jsx";
import EventTimeline from "../components/EventTimeline.jsx";
import CommentThread from "../components/CommentThread.jsx";
import CompleteTaskDialog from "../components/CompleteTaskDialog.jsx";
import TasksTab from "../components/TasksTab.jsx";
import ProgressiveStatusControl from "../components/ProgressiveStatusControl.jsx";

const TABS = [
  { key: "overview", label: "overview" },
  { key: "tasks_contacts", labelStatic: "Tarefas & Contactos" },
  { key: "comments_attachments", labelStatic: "Comentários & Anexos" },
  { key: "audit", label: "audit_log" },
];

// Build tab label with count badges based on counts loaded by the parent.
function tabLabelWithCount(t, tb, c, counts) {
  if (tb.labelStatic) {
    if (tb.key === "tasks_contacts") {
      const tasks = counts?.tasks ?? null;
      const contacts = counts?.contacts ?? (c.customer_contacts || []).length;
      if (tasks === null) return tb.labelStatic;
      return `${tb.labelStatic} (${tasks}·${contacts})`;
    }
    if (tb.key === "comments_attachments") {
      const comments = counts?.comments ?? null;
      const attachments = counts?.attachments ?? null;
      if (comments === null || attachments === null) return tb.labelStatic;
      return `${tb.labelStatic} (${comments}·${attachments})`;
    }
    return tb.labelStatic;
  }
  return t(tb.label);
}

export default function ServiceCaseDetailPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const [c, setCase] = useState(null);
  const [tab, setTab] = useState("overview");
  const [enums, setEnums] = useState(null);
  const [users, setUsers] = useState([]);
  const [autoTaskPlan, setAutoTaskPlan] = useState(null);
  const [decisionPrompt, setDecisionPrompt] = useState(null);   // { kind: "pcp"|"pec" }
  const [showCreatePcp, setShowCreatePcp] = useState(false);
  const [showCreatePec, setShowCreatePec] = useState(false);
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);
  const [tabCounts, setTabCounts] = useState({ tasks: null, comments: null, attachments: null });

  const load = useCallback(async () => {
    const r = await api.get(`/service-cases/${id}`);
    setCase(r.data);
  }, [id]);

  useEffect(() => {
    load();
    api.get("/enums").then((r) => setEnums(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, [id, load]);

  // Lazy-load counts for the tab badges (refreshes when tasksRefreshKey bumps).
  useEffect(() => {
    if (!id) return;
    Promise.all([
      api.get(`/tasks?service_case_id=${id}&status=Aberta`).then((r) => r.data.length).catch(() => 0),
      api.get(`/comments?record_type=service_case&record_id=${id}`).then((r) => r.data.length).catch(() => 0),
      api.get(`/attachments?record_type=service_case&record_id=${id}`).then((r) => r.data.length).catch(() => 0),
      api.get(`/service-cases/${id}/contacts`).then((r) => r.data.length).catch(() => 0),
    ]).then(([tasks, comments, attachments, contacts]) => {
      setTabCounts({ tasks, comments, attachments, contacts });
    });
  }, [id, tasksRefreshKey, c]);

  if (!c || !enums) return <div className="p-6 text-sm text-[#6C757D]">Loading…</div>;

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm" data-testid="case-sticky-header">
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <Link to="/service-cases" className="flex items-center gap-1 hover:text-[#1F3864]">
            <ChevronLeft className="w-3 h-3" /> Service Cases
          </Link>
          <span>/</span>
          <span className="font-mono">{c.number}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-xl font-bold text-[#1A1A2E]" data-testid="case-customer-name">{c.customer_name}</h1>
              <PriorityBadge priority={c.priority} />
              <StatusBadge status={c.current_status} maxWidth="200px" />
              <FeedbackChip lastContactIso={c.last_customer_contact} size="lg" />
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6C757D]">
              <span className="font-mono">{c.vehicle_registration}</span>
              <span className="font-mono">VIN {c.vin}</span>
              <span>{c.brand} {c.model}</span>
              <span>{c.branch}</span>
              <span className="font-mono">{c.alidata_job_card}</span>
              {c.service_advisor && <span>SA: {c.service_advisor.name}</span>}
              {(c.linked_swcs || []).length > 0 && <WarrantyHeaderBadge swcs={c.linked_swcs} />}
              {(c.active_mpm_plans || []).length > 0 && <MpmHeaderBadge plans={c.active_mpm_plans} />}
              {c.vr_record && <VrWarrantyBadge vr={c.vr_record} />}
            </div>
            {c.next_action && c.current_status !== "Encerrado" && (
              <div className="mt-2 inline-flex items-center gap-2 bg-[#F4F6F8] border border-gray-200 rounded-sm px-2 py-1 text-xs">
                <span className="font-semibold uppercase tracking-wider text-[#6C757D]">Next:</span>
                <span className="text-[#1A1A2E]">{c.next_action}</span>
                {c.next_action_responsible && <span className="text-[#6C757D]">· {c.next_action_responsible.name}</span>}
                {c.next_action_deadline && <span className="text-[#6C757D]">· {fmtDateTime(c.next_action_deadline)}</span>}
              </div>
            )}
            {(c.active_mpm_plans || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="mpm-banner">
                {c.active_mpm_plans.map((p) => (
                  <Link
                    key={p.id}
                    to={`/maintenance-plans?open=${p.id}`}
                    className="inline-flex items-center gap-2 bg-[#FFF7E6] border border-[#FFC107]/40 text-[#856404] rounded-sm px-2.5 py-1 text-xs hover:bg-[#FFEFC2] transition-colors"
                    data-testid={`mpm-banner-link-${p.id}`}
                  >
                    <CalendarRange className="w-3.5 h-3.5" />
                    <span>This vehicle has an active MPM plan: <b>{p.number}</b> · {p.plan_name}</span>
                    <span className="font-mono text-[10px]">{p.consumed_services}/{p.total_services} used</span>
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Progressive workflow control */}
        <div className="mt-3">
          <ProgressiveStatusControl
            entity="som"
            recordId={id}
            currentStatus={c.current_status}
            onAdvance={async (newStatus, comment) => {
              const { data } = await api.post(`/service-cases/${id}/status`, {
                new_status: newStatus,
                comment,
                cancel_reason: newStatus === "Cancelado" ? comment : undefined,
                delivery_confirmed: newStatus === "Encerrado" ? true : undefined,
                customer_informed: newStatus === "Encerrado" ? true : undefined,
              });
              await load();
              if (data?.decision_prompt) {
                setDecisionPrompt(data.decision_prompt);
              } else if (data?.planned_auto_task) {
                setAutoTaskPlan(data.planned_auto_task);
              }
            }}
          />
        </div>

        {/* Tabs */}
        <div className="mt-3 border-b -mb-3 border-gray-200 flex gap-5 overflow-x-auto">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              data-testid={`case-tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === tb.key ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"
              }`}
            >
              {tabLabelWithCount(t, tb, c, tabCounts)}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {tab === "overview" && <OverviewTab case_={c} reload={load} users={users} enums={enums} />}
        {tab === "tasks_contacts" && <TasksAndContactsTab caseId={id} users={users} case_={c} enums={enums} reload={load} refreshKey={tasksRefreshKey} />}
        {tab === "comments_attachments" && <CommentsAndAttachmentsTab caseId={id} />}
        {tab === "audit" && <AuditTab recordType="service_case" recordId={id} />}
      </div>

      {autoTaskPlan && (
        <AutoTaskConfirmDialog
          plan={autoTaskPlan}
          users={users}
          onClose={() => setAutoTaskPlan(null)}
          onConfirm={async (taskPayload) => {
            await api.post("/tasks", taskPayload);
            setAutoTaskPlan(null);
            setTab("tasks_contacts");
            setTasksRefreshKey((k) => k + 1);
          }}
        />
      )}

      {decisionPrompt && (
        <WorkflowDecisionDialog
          kind={decisionPrompt.kind}
          caseNumber={c.number}
          onClose={() => setDecisionPrompt(null)}
          onCreate={() => {
            const kind = decisionPrompt.kind;
            setDecisionPrompt(null);
            if (kind === "pcp") setShowCreatePcp(true);
            else setShowCreatePec(true);
          }}
          onSkip={async () => {
            const kind = decisionPrompt.kind;
            const path =
              kind === "pcp"
                ? `/service-cases/${id}/skip-classification`
                : `/service-cases/${id}/skip-parts-request`;
            try {
              await api.post(path);
            } finally {
              setDecisionPrompt(null);
              load();
            }
          }}
        />
      )}

      {showCreatePcp && (
        <NewClassificationRequestDialog
          case_={c}
          onClose={() => setShowCreatePcp(false)}
          onCreated={async () => {
            setShowCreatePcp(false);
            await load();
          }}
        />
      )}

      {showCreatePec && (
        <NewPartsRequestDialog
          presetCaseId={id}
          onClose={() => setShowCreatePec(false)}
          onCreated={async () => {
            setShowCreatePec(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function AutoTaskConfirmDialog({ plan, users, onClose, onConfirm }) {
  // Format ISO -> input datetime-local "YYYY-MM-DDTHH:MM"
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  };
  const [responsibleId, setResponsibleId] = useState(plan.responsible_user_id || "");
  const [deadlineLocal, setDeadlineLocal] = useState(toLocalInput(plan.deadline));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const activeUsers = users.filter((u) => !u.deactivated);
  const selectedUser = activeUsers.find((u) => u.id === responsibleId);

  const submit = async () => {
    setError("");
    if (!responsibleId) {
      setError("Responsible user is required.");
      return;
    }
    if (!deadlineLocal) {
      setError("Deadline is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        title: plan.title,
        description: plan.description,
        service_case_id: plan.service_case_id,
        linked_module: plan.linked_module,
        linked_record_id: plan.linked_record_id,
        responsible_user_id: responsibleId,
        responsible_department: plan.responsible_department,
        priority: plan.priority || "Normal",
        deadline: new Date(deadlineLocal).toISOString(),
      });
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="auto-task-dialog">
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8]">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Auto-task triggered</div>
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">Confirm task creation</h2>
          <div className="text-xs text-[#6C757D] mt-0.5">
            Status changed to <span className="font-semibold text-[#1A1A2E]">&ldquo;{plan.triggered_by_status}&rdquo;</span> — a follow-up task will be created.
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Task title</div>
            <div className="text-sm font-semibold text-[#1A1A2E]" data-testid="auto-task-title">{plan.title}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Department</div>
            <div className="text-sm text-[#1A1A2E]">{plan.responsible_department}</div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Responsible user *</span>
            <select
              value={responsibleId}
              onChange={(e) => setResponsibleId(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="auto-task-responsible"
            >
              <option value="">Select…</option>
              {activeUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.role.replaceAll("_", " ")}{u.branch ? `, ${u.branch}` : ""})
                </option>
              ))}
            </select>
            {plan.default_role && (!selectedUser || selectedUser.role !== plan.default_role) && (
              <div className="text-[11px] text-[#B45309] mt-0.5">
                Suggested role: {plan.default_role.replaceAll("_", " ")}
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Deadline *</span>
            <input
              type="datetime-local"
              value={deadlineLocal}
              onChange={(e) => setDeadlineLocal(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="auto-task-deadline"
            />
            {plan.default_deadline_hours != null && (
              <div className="text-[11px] text-[#6C757D] mt-0.5">
                Default SLA: {plan.default_deadline_hours} hour{plan.default_deadline_hours === 1 ? "" : "s"} from now
              </div>
            )}
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Priority</div>
            <div className="text-sm text-[#1A1A2E]">{plan.priority || "Normal"}</div>
          </div>
          {error && (
            <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="auto-task-error">
              {error}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-between gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8] text-[#6C757D]"
            data-testid="auto-task-skip"
            disabled={submitting}
          >
            Skip task
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60 flex items-center gap-2"
            data-testid="auto-task-confirm"
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            Create task
          </button>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ case_, reload, users, enums }) {
  const [showNewPr, setShowNewPr] = useState(false);
  return (
    <div>
      <WorkflowTimeline som={case_} />
      <FuelRequestsSection caseId={case_.id} caseNumber={case_.number} />
      <EventTimeline caseId={case_.id} caseNumber={case_.number} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-gray-200 rounded-md p-4">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Customer & vehicle</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Cell label="Customer" value={case_.customer_name} />
          <Cell label="Phone" value={case_.customer_phone} mono />
          <Cell label="Email" value={case_.customer_email || "—"} />
          <Cell label="Branch" value={case_.branch} />
          <Cell label="Registration" value={case_.vehicle_registration} mono />
          <Cell label="VIN" value={case_.vin} mono />
          <Cell label="Brand" value={`${case_.brand} ${case_.model}`} />
          <Cell label="Mileage" value={`${case_.mileage} km`} mono />
          <Cell label="Alidata job card" value={case_.alidata_job_card} mono />
          <Cell label="Opened" value={fmtDateTime(case_.opened_at)} />
        </div>
        <div className="mt-4 border-t border-gray-100 pt-4">
          <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider">Customer complaint</h3>
          <p className="text-sm text-[#1A1A2E] whitespace-pre-wrap">{case_.customer_complaint}</p>
        </div>
        {case_.internal_notes && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider">Internal notes</h3>
            <p className="text-sm text-[#1A1A2E] whitespace-pre-wrap">{case_.internal_notes}</p>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-wider">Six-question rule</h3>
          <div className="space-y-2 text-sm">
            <Cell label="Current status" value={<StatusBadge status={case_.current_status} />} inline />
            <Cell label="Responsible" value={case_.next_action_responsible?.name || "—"} inline />
            <Cell label="Next action" value={case_.next_action || "—"} inline />
            <Cell label="Deadline" value={case_.next_action_deadline ? fmtDateTime(case_.next_action_deadline) : "—"} inline />
            <Cell label="Last contact" value={case_.last_customer_contact ? fmtRelative(case_.last_customer_contact) : "—"} inline />
            <Cell label="Service Advisor" value={case_.service_advisor?.name || "—"} inline />
          </div>
        </div>
        {case_.alidata_quotation_number && (
          <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="som-alidata-quotation">
            <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider">Cotação Alidata</h3>
            <div className="font-mono text-sm text-[#1F3864] font-semibold">{case_.alidata_quotation_number}</div>
          </div>
        )}
        {(case_.linked_pcps || []).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="som-linked-pcps">
            <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider">Classificações de Peças ({case_.linked_pcps.length})</h3>
            <div className="space-y-1.5">
              {case_.linked_pcps.map((p) => (
                <Link
                  key={p.id}
                  to={`/classification-requests?focus=${p.id}&from=som`}
                  className="flex items-center justify-between text-xs hover:bg-[#F4F6F8] -mx-2 px-2 py-1 rounded-sm"
                  data-testid={`som-pcp-${p.id}`}
                >
                  <span className="font-mono text-[#1F3864] font-semibold">{p.number}</span>
                  <StatusBadge status={p.status} />
                </Link>
              ))}
            </div>
          </div>
        )}
        {(case_.linked_pecs || []).length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="som-linked-pecs">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-sm font-bold text-[#1A1A2E] uppercase tracking-wider">Encomendas de Peças ({case_.linked_pecs.length})</h3>
              <button
                onClick={() => setShowNewPr(true)}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#1F3864] font-semibold hover:underline"
                data-testid="case-new-pr-btn"
              >
                <Plus className="w-3 h-3" /> Nova Encomenda de Peças
              </button>
            </div>
            <div className="space-y-1.5">
              {case_.linked_pecs.map((p) => (
                <Link
                  key={p.id}
                  to={`/parts-requests/${p.id}`}
                  state={{ from: "som" }}
                  className="flex items-center justify-between text-xs hover:bg-[#F4F6F8] -mx-2 px-2 py-1 rounded-sm"
                  data-testid={`som-pec-${p.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[#1F3864] font-semibold">{p.number}</span>
                    {p.appointment_date && (
                      <span className="text-[10px] text-[#6C757D] truncate" data-testid={`som-pec-appointment-${p.id}`}>
                        · veículo agendado para {(() => {
                          const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(p.appointment_date);
                          return m ? `${m[3]}/${m[2]}/${m[1]}` : fmtDate(p.appointment_date);
                        })()}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={p.status} />
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md p-4 text-center" data-testid="som-pec-empty">
            <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider">Encomendas de Peças</h3>
            <button
              onClick={() => setShowNewPr(true)}
              className="inline-flex items-center gap-1 text-xs bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm font-medium"
              data-testid="case-new-pr-btn-empty"
            >
              <Plus className="w-3 h-3" /> Nova Encomenda de Peças
            </button>
          </div>
        )}
        {(case_.linked_swcs || []).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="som-linked-swcs">
            <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider">Garantias ({case_.linked_swcs.length})</h3>
            <div className="space-y-1.5">
              {case_.linked_swcs.map((s) => (
                <Link
                  key={s.id}
                  to={`/warranty-claims?focus=${s.id}&from=som`}
                  className="flex items-center justify-between gap-2 text-xs hover:bg-[#F4F6F8] -mx-2 px-2 py-1 rounded-sm"
                  data-testid={`som-swc-${s.id}`}
                >
                  <span className="min-w-0">
                    <span className="font-mono text-[#1F3864] font-semibold">{s.number}</span>
                    {s.claim_type && <span className="ml-2 text-[10px] text-[#6C757D] uppercase tracking-wider">{s.claim_type}</span>}
                  </span>
                  <StatusBadge status={s.status} />
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider">Feedback</h3>
          <FeedbackChip lastContactIso={case_.last_customer_contact} />
        </div>
        <MaintenancePlanSection plans={case_.active_mpm_plans} />
      </div>
      </div>
      {showNewPr && (
        <NewPartsRequestDialog
          presetCaseId={case_.id}
          onClose={() => setShowNewPr(false)}
          onCreated={() => { setShowNewPr(false); reload(); }}
        />
      )}
    </div>
  );
}

// "Plano de Manutenção" section at the bottom of the Overview tab.
// Renders one summary card per active MPM plan or an empty-state hint.
function MaintenancePlanSection({ plans }) {
  const list = plans || [];
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4 lg:col-span-3" data-testid="som-mpm-section">
      <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2 uppercase tracking-wider flex items-center gap-2">
        <CalendarRange className="w-4 h-4 text-[#1F3864]" />
        Plano de Manutenção
      </h3>
      {list.length === 0 ? (
        <div className="text-xs text-[#6C757D]" data-testid="som-mpm-empty">Sem plano de manutenção activo para esta viatura.</div>
      ) : (
        <div className="space-y-3">
          {list.map((p) => <MaintenancePlanCard key={p.id} plan={p} />)}
        </div>
      )}
    </div>
  );
}

function MaintenancePlanCard({ plan }) {
  const total = Math.max(1, plan.total_services || 0);
  const consumed = plan.consumed_services || 0;
  const pct = plan.consumed_pct ?? Math.min(100, Math.round((consumed / total) * 100));
  const days = plan.days_to_expiry;
  const expired = days != null && days < 0;
  const expiringSoon = days != null && days >= 0 && days <= 30;
  const exhausted = consumed >= (plan.total_services || 0) && (plan.total_services || 0) > 0;
  const expiryClass = expired || exhausted ? "text-[#DC3545] font-semibold" : expiringSoon ? "text-[#856404] font-semibold" : "text-[#1A1A2E]";
  return (
    <Link
      to={`/maintenance-plans?open=${plan.id}`}
      className="block border border-gray-200 rounded-md p-3 hover:border-[#1F3864] transition-colors"
      data-testid={`som-mpm-card-${plan.id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-mono text-[#1F3864] font-bold text-sm">{plan.number}</div>
          <div className="text-xs text-[#1A1A2E]">{plan.plan_name || "—"}</div>
        </div>
        <StatusBadge status={plan.status} />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Validade</div>
          <div className={expiryClass}>
            {plan.valid_to ? fmtDate(plan.valid_to) : "—"}
            {days != null && (
              <span className="ml-1 text-[10px] uppercase tracking-wider">
                {expired ? `(expirado há ${Math.abs(days)} dia${Math.abs(days) === 1 ? "" : "s"})` :
                 expiringSoon ? `(expira em ${days} dia${days === 1 ? "" : "s"})` : ""}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Serviços</div>
          <div className="text-[#1A1A2E]">{consumed} de {plan.total_services || 0} utilizados</div>
        </div>
        {plan.next_entitlement && (
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Próximo serviço disponível</div>
            <div className="text-[#1A1A2E]">
              {plan.next_entitlement.label || `Serviço #${plan.next_entitlement.sequence_number || ""}`}
            </div>
          </div>
        )}
      </div>
      <div className="mt-2">
        <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${exhausted ? "bg-[#DC3545]" : pct >= 75 ? "bg-[#FFC107]" : "bg-[#1F3864]"}`}
            style={{ width: `${Math.min(100, pct)}%` }}
            data-testid={`som-mpm-progress-${plan.id}`}
          />
        </div>
        <div className="text-[10px] text-[#6C757D] mt-0.5">{pct}% consumido</div>
      </div>
    </Link>
  );
}

function Cell({ label, value, mono, inline }) {
  return (
    <div className={inline ? "flex items-start gap-2" : ""}>
      <div className={`text-[10px] uppercase tracking-widest text-[#6C757D] ${inline ? "min-w-[110px]" : ""}`}>{label}</div>
      <div className={`text-sm font-medium text-[#1A1A2E] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}



function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm" data-testid={testid} />
    </label>
  );
}

function ContactsTab({ case_, enums, reload }) {
  const [contacts, setContacts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = async () => {
    const { data } = await api.get(`/service-cases/${case_.id}/contacts`);
    setContacts(data);
  };
  useEffect(() => { load(); }, [case_.id]);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E]">Customer contacts ({contacts.length})</h3>
        <button onClick={() => setShowNew(true)} className="text-xs bg-[#1F3864] text-white px-2.5 py-1.5 rounded-sm hover:bg-[#142542] flex items-center gap-1" data-testid="add-contact-btn">
          <Plus className="w-3 h-3" /> Log contact
        </button>
      </div>
      <div className="space-y-3">
        {contacts.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-md p-6 text-center text-sm text-[#6C757D]">No contacts logged yet.</div>
        ) : contacts.map((c) => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-md p-3" data-testid={`contact-${c.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <StatusBadge status={c.contact_result} />
                <span className="text-xs text-[#6C757D]">{c.contact_method}</span>
              </div>
              <span className="text-xs text-[#6C757D]">{fmtDateTime(c.occurred_at)}</span>
            </div>
            <div className="text-sm text-[#1A1A2E] whitespace-pre-wrap">{c.summary}</div>
            <div className="text-xs text-[#6C757D] mt-1">By {c.registered_by_name}</div>
          </div>
        ))}
      </div>
      {showNew && <NewContactDialog caseId={case_.id} enums={enums} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); reload(); }} />}
    </div>
  );
}

function NewContactDialog({ caseId, enums, onClose, onCreated }) {
  const [form, setForm] = useState({
    contact_method: "Phone",
    contact_result: "Contacted",
    summary: "",
    next_followup_date: "",
    no_evidence_justification: "",
    partial_approval_note: "",
    rejection_reason: "",
  });
  const [error, setError] = useState("");

  const needsFollowup = ["No answer", "Asked for time"].includes(form.contact_result);
  const needsRejReason = form.contact_result === "Rejected";
  const needsPartial = form.contact_result === "Partial approval";
  const needsApprEvidence = form.contact_result === "Approved";

  const submit = async () => {
    setError("");
    try {
      await api.post(`/service-cases/${caseId}/contacts`, {
        ...form,
        service_case_id: caseId,
        next_followup_date: form.next_followup_date ? new Date(form.next_followup_date).toISOString() : null,
      });
      onCreated();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="new-contact-dialog">
        <div className="px-5 py-3 border-b border-gray-200"><h2 className="font-heading text-lg font-bold">Log customer contact</h2></div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Method</span>
            <select value={form.contact_method} onChange={(e) => setForm({ ...form, contact_method: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm" data-testid="contact-method">
              {enums.contact_methods.map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Result</span>
            <select value={form.contact_result} onChange={(e) => setForm({ ...form, contact_result: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm" data-testid="contact-result">
              {enums.contact_results.map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Summary *</span>
            <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm h-16" data-testid="contact-summary" />
          </label>
          {needsFollowup && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Next follow-up date *</span>
              <input type="datetime-local" value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm" />
            </label>
          )}
          {needsRejReason && (
            <Input label="Rejection reason *" value={form.rejection_reason} onChange={(v) => setForm({ ...form, rejection_reason: v })} />
          )}
          {needsPartial && (
            <Input label="Partial approval note *" value={form.partial_approval_note} onChange={(v) => setForm({ ...form, partial_approval_note: v })} />
          )}
          {needsApprEvidence && (
            <Input label="No-evidence justification (or upload evidence later)" value={form.no_evidence_justification} onChange={(v) => setForm({ ...form, no_evidence_justification: v })} />
          )}
          {error && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] rounded-sm text-sm">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancel</button>
          <button onClick={submit} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm" data-testid="contact-submit">Register</button>
        </div>
      </div>
    </div>
  );
}

// ===== Combined tab wrappers (iteração 29) =====

function SectionDivider({ label, count, action, testidKey }) {
  const key = testidKey || label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-");
  return (
    <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-3 mt-6 first:mt-0" data-testid={`section-divider-${key}`}>
      <h3 className="font-heading text-base font-bold text-[#1A1A2E] uppercase tracking-wider">
        {label}{count != null && <span className="text-[#6C757D] font-normal ml-1.5">({count})</span>}
      </h3>
      {action}
    </div>
  );
}

function TasksAndContactsTab({ caseId, users, case_, enums, reload, refreshKey }) {
  return (
    <div data-testid="tasks-and-contacts-tab" className="space-y-2">
      <TasksTab caseId={caseId} users={users} refreshKey={refreshKey} />
      <div className="border-t-2 border-gray-200 my-6" aria-hidden="true" />
      <ContactsTab case_={case_} enums={enums} reload={reload} />
    </div>
  );
}

function CommentsAndAttachmentsTab({ caseId }) {
  return (
    <div data-testid="comments-and-attachments-tab" className="space-y-2">
      <SectionDivider label="Comentários" />
      <CommentThread recordType="service_case" recordId={caseId} />
      <div className="border-t-2 border-gray-200 my-6" aria-hidden="true" />
      <SectionDivider label="Outros Anexos" />
      <AttachmentsTab recordType="service_case" recordId={caseId} />
    </div>
  );
}

function AuditTab({ recordType, recordId }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get(`/audit-log?record_type=${recordType}&record_id=${recordId}`).then((r) => setEntries(r.data));
  }, [recordType, recordId]);
  return (
    <div>
      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3">Audit history ({entries.length})</h3>
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Action</th>
              <th className="px-3 py-2 text-left">Detail</th>
              <th className="px-3 py-2 text-left">Comment</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-[#6C757D]">No audit entries</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} className="border-t border-gray-100" data-testid={`audit-${e.id}`}>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDateTime(e.timestamp)}</td>
                <td className="px-3 py-2 text-xs">{e.user_name} <span className="text-[#6C757D]">({e.user_role})</span></td>
                <td className="px-3 py-2 text-xs">{e.action}</td>
                <td className="px-3 py-2 text-xs text-[#6C757D]">
                  {e.old_value != null && <>from: <span className="font-mono">{JSON.stringify(e.old_value).slice(0, 40)}</span><br /></>}
                  {e.new_value != null && <>to: <span className="font-mono">{JSON.stringify(e.new_value).slice(0, 40)}</span></>}
                </td>
                <td className="px-3 py-2 text-xs">{e.comment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AttachmentsTab({ recordType, recordId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = async () => {
    const { data } = await api.get(`/attachments?record_type=${recordType}&record_id=${recordId}`);
    setFiles(data);
  };
  useEffect(() => { load(); }, [recordType, recordId]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("record_type", recordType);
    fd.append("record_id", recordId);
    try {
      await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E]">Attachments ({files.length})</h3>
        <label className="text-xs bg-[#1F3864] text-white px-2.5 py-1.5 rounded-sm hover:bg-[#142542] flex items-center gap-1 cursor-pointer" data-testid="upload-attachment">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          Upload
          <input type="file" hidden onChange={onFile} />
        </label>
      </div>
      <div className="bg-white border border-gray-200 rounded-md">
        {files.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#6C757D]">No attachments</div>
        ) : files.map((f) => (
          <div key={f.id} className="px-3 py-2 border-b border-gray-100 last:border-0 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-[#1F3864]" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{f.original_filename}</div>
                <div className="text-[10px] text-[#6C757D]">{(f.size / 1024).toFixed(1)} KB · {f.uploaded_by_name} · {fmtRelative(f.uploaded_at)}</div>
              </div>
            </div>
            <a
              href={`${api.defaults.baseURL}/attachments/${f.id}/download`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#1F3864] hover:underline"
              data-testid={`attachment-download-${f.id}`}
            >
              Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}


// Small badge in the SOM header showing the overall warranty state.
// Spec: yellow = SWC activa (in progress) · red = SWC problema (rejected/info-needed/incomplete) · grey = SWC encerrada (all closed/cancelled)
function WarrantyHeaderBadge({ swcs }) {
  const closed = new Set(["Encerrado", "Cancelado"]);
  const problem = new Set(["Não aprovado", "Informação adicional solicitada", "Incompleto"]);
  const inProgress = new Set([
    "Evidências em recolha",
    "Submetido à marca",
    "Aguarda resposta da marca",
    "Reparação autorizada",
    "Reparação concluída",
    "Aprovado",
    "Faturado",
  ]);
  const hasProblem = swcs.some((s) => problem.has(s.status));
  const hasInProgress = swcs.some((s) => inProgress.has(s.status));
  let color, label;
  if (hasProblem) {
    color = "bg-[#F8D7DA] text-[#721C24] border-[#DC3545]/40";
    label = "SWC problema";
  } else if (hasInProgress) {
    color = "bg-[#FFF3CD] text-[#856404] border-[#FFC107]/40";
    label = "SWC activa";
  } else if (swcs.every((s) => closed.has(s.status))) {
    color = "bg-[#E2E8F0] text-[#495057] border-gray-300";
    label = "SWC encerrada";
  } else {
    color = "bg-[#FFF3CD] text-[#856404] border-[#FFC107]/40";
    label = "SWC activa";
  }
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${color}`} data-testid="warranty-header-badge">
      {label}
    </span>
  );
}

// Badge in the SOM header summarising the MPM plan state.
// Green = active with services left · Yellow = expires within 30 days · Red = expired or exhausted · null = no plan
function MpmHeaderBadge({ plans }) {
  if (!plans || plans.length === 0) return null;
  // Take the most relevant plan (red > yellow > green)
  let level = "green";
  for (const p of plans) {
    const exhausted = (p.consumed_services || 0) >= (p.total_services || 0) && (p.total_services || 0) > 0;
    const expired = p.days_to_expiry != null && p.days_to_expiry < 0;
    const expiringSoon = p.days_to_expiry != null && p.days_to_expiry >= 0 && p.days_to_expiry <= 30;
    if (exhausted || expired) { level = "red"; break; }
    if (expiringSoon) level = "yellow";
  }
  const map = {
    green: { color: "bg-[#D4EDDA] text-[#155724] border-[#28A745]/40", label: "MPM activo" },
    yellow: { color: "bg-[#FFF3CD] text-[#856404] border-[#FFC107]/40", label: "MPM expira em breve" },
    red: { color: "bg-[#F8D7DA] text-[#721C24] border-[#DC3545]/40", label: "MPM expirado" },
  };
  const cfg = map[level];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${cfg.color}`} data-testid="mpm-header-badge">
      {cfg.label}
    </span>
  );
}

// Badge showing the warranty status of the vehicle from the linked VR record.
function VrWarrantyBadge({ vr }) {
  if (!vr) return null;
  const cfg = {
    active: { color: "bg-[#D4EDDA] text-[#155724] border-[#28A745]/40", label: "GARANTIA ACTIVA" },
    expiring_soon: { color: "bg-[#FFF3CD] text-[#856404] border-[#FFC107]/40", label: "GARANTIA EXPIRA EM BREVE" },
    expired: { color: "bg-[#F8D7DA] text-[#721C24] border-[#DC3545]/40", label: "GARANTIA EXPIRADA" },
    unknown: { color: "bg-gray-100 text-gray-600 border-gray-300", label: "GARANTIA S/ INFO" },
  }[vr.warranty_status || "unknown"];
  return (
    <Link
      to={`/vehicle-registrations/${vr.id}`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 hover:opacity-80 ${cfg.color}`}
      data-testid="vr-warranty-badge"
      title={`Ligado a ${vr.vr_number}`}
    >
      {cfg.label}
    </Link>
  );
}

